import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Brand } from '@fametrue/shared';

/**
 * Pannello "Analisi sintetica" del Level 1.
 *
 * Nel riferimento il pannello è alto quanto il suo testo, e con un'analisi
 * lunga la colonna destra del `.kpi-split` sfora sotto le card degli indici.
 * Su richiesta del committente qui l'altezza è vincolata: il pannello riempie
 * lo spazio che resta nella colonna (vedi `.kpi-split-right` in app.css), il
 * testo che non ci sta viene ritagliato con una sfumatura e un pulsante
 * "Mostra altro" apre una modale con l'analisi completa. Divergenza deliberata
 * dal v40.
 *
 * Il pulsante compare solo quando il testo è davvero ritagliato: si misura
 * `scrollHeight` contro `clientHeight` e si rimisura a ogni ridimensionamento,
 * perché la larghezza della colonna cambia il numero di righe.
 */
export function AnalysisPanel({ brand }: { brand: Brand }): ReactNode {
  const { t } = useTranslation();
  const body = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    const measure = (): void => {
      setClipped(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [brand.analysis]);

  return (
    <div className="kpi-verdict analysis-panel">
      <EngravedGrid />
      <div className={`analysis-body${clipped ? ' clipped' : ''}`} ref={body}>
        {/* Il testo di analisi arriva dal JSON con HTML inline consentito
            (spec §6): i testi contengono <strong>, e va reso. */}
        <div className="analysis-text" dangerouslySetInnerHTML={{ __html: brand.analysis }} />
      </div>
      {clipped ? (
        <div className="analysis-more-row">
          <button type="button" className="analysis-more" onClick={() => setOpen(true)}>
            {t('insight.showMore')}
          </button>
        </div>
      ) : null}
      {open ? <AnalysisModal brand={brand} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

/**
 * Modale con l'analisi completa. Va in portal su `body`: `.kpi-verdict` ha
 * `overflow: hidden` e `isolation: isolate`, quindi un overlay annidato lì
 * dentro resterebbe ritagliato e sotto il resto della pagina.
 */
function AnalysisModal({ brand, onClose }: { brand: Brand; onClose: () => void }): ReactNode {
  const { t } = useTranslation();
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButton.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="analysis-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="modal-eyebrow">{brand.name}</div>
            <h2 className="modal-title" id="analysis-modal-title">
              {t('insight.analysis')}
            </h2>
          </div>
          <button
            type="button"
            className="modal-close"
            aria-label={t('insight.close')}
            onClick={onClose}
            ref={closeButton}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <div className="analysis-text" dangerouslySetInnerHTML={{ __html: brand.analysis }} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Reticolo inciso sui pannelli navy: nel riferimento è un div assoluto. */
export function EngravedGrid(): ReactNode {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        pointerEvents: 'none',
        borderRadius: 'inherit',
      }}
    />
  );
}
